import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ProfileState } from '../state/profile.state';
import { Store } from '@ngxs/store';
import {
  TimeoutError,
  catchError,
  filter,
  first,
  map,
  of,
  tap,
  timeout,
} from 'rxjs';

/**
 * Gates the entire `apps/admin` `/app/*` tree (users, companies, admin-tools, stamps,
 * docprep) on the system-wide `Profile.IsEtAdmin` flag. Redirects to the login page with
 * an error flag rather than a dedicated "forbidden" page, mirroring how JwtGuard already
 * redirects unauthenticated users to `/auth/login`.
 *
 * Waits for `selfProfile` to actually be populated before deciding, mirroring
 * `ProfileResolver`'s wait-for-load pattern. `AppComponent.bootstrapBetterAuth`/
 * `bootstrapMsal` dispatch `SetSelfProfile` asynchronously (after the `getSelfProfile()`
 * API call resolves), which usually lands *after* this guard first runs on initial
 * navigation. `ProfileState.getIsEtAdmin` can't distinguish "not loaded yet" from "loaded
 * and false" (both read as `false`), so selecting it directly with `first()` would fire on
 * the store's pre-load default and incorrectly reject every EtAdmin on a fresh page load.
 */
export const etAdminGuard: CanActivateFn = () => {
  const router = inject(Router);
  return inject(Store)
    .select(ProfileState.getSelfProfile)
    .pipe(
      map((selfProfile) => (selfProfile ? selfProfile.isEtAdmin : undefined)),
      filter((isEtAdmin): isEtAdmin is boolean => isEtAdmin !== undefined),
      tap((isEtAdmin) => {
        if (!isEtAdmin) {
          router.navigate(['/auth/login'], {
            queryParams: { error: 'not_admin' },
          });
        }
      }),
      first(),
      timeout({ first: 15_000 }),
      catchError((err) => {
        if (err instanceof TimeoutError) {
          router.navigate(['/auth/login'], {
            queryParams: { error: 'session_timeout' },
          });
        }
        return of(false);
      }),
    );
};
